/**
 * Aviso al conseguir un logro (FR-ACH-03).
 *
 * Los logros son derivados (FR-ACH-02): se recalculan siempre desde los resultados y
 * pueden revocarse si se edita el historial. Aquí solo se compara el conjunto actual
 * con el que ya se había visto, para avisar una vez de los nuevos.
 */
import { useEffect, useMemo, useRef } from 'react';
import { achievements, addDays, streaks, type LocalDate, type UEKind } from '@habitos/engine';
import { getMeta, setMeta } from '../db/db';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { useToast } from '../ui/Toast';
import { toEngineSettings } from './model';
import { evaluate, liveHabits } from './selectors';
import { streakText } from '../ui/format';

const SEEN_KEY = 'achievements:seen';

/** Texto de un logro a partir de su identificador (`streak_7`, `total_100`). */
export function achievementLabel(t: ReturnType<typeof useI18n>['t'], id: string, unit: UEKind): string {
  const n = Number(id.split('_')[1] ?? 0);
  return id.startsWith('streak_') ? t('achievements.streak', { count: streakText(t, n, unit) }) : t('achievements.total', { count: n });
}

/**
 * Avisa de los logros nuevos. En la primera ejecución solo guarda la línea base: quien
 * ya tenía logros al actualizar la app no recibe una avalancha de avisos.
 */
export function useAchievementToast(): void {
  const { t } = useI18n();
  const { data, ctx, idx } = useApp();
  const toast = useToast();
  // Evita avisar dos veces mientras la escritura en `meta` está en vuelo.
  const seen = useRef<Set<string> | null>(null);

  const current = useMemo(() => {
    if (!ctx) return null;
    const settings = toEngineSettings(ctx.settings);
    const out = new Map<string, { habit: string; unit: UEKind; achievedOn: LocalDate }>();
    for (const h of liveHabits(data.habits)) {
      if (h.kind === 'task' || h.archivedAt) continue;
      const results = evaluate(h, idx, data.vacations, settings, ctx.today);
      const unit = streaks(results).unit;
      for (const a of achievements(results)) out.set(`${h.id}:${a.id}`, { habit: h.name, unit, achievedOn: a.achievedOn });
    }
    return out;
  }, [data.habits, data.vacations, idx, ctx]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    void (async () => {
      if (!seen.current) {
        const stored = await getMeta<string[] | null>(SEEN_KEY, null);
        if (cancelled) return;
        if (stored === null) {
          // Primera vez: línea base silenciosa.
          seen.current = new Set(current.keys());
          await setMeta(SEEN_KEY, [...seen.current]);
          return;
        }
        seen.current = new Set(stored);
      }
      // FR-ACH-03: solo se celebra lo conseguido hoy o ayer. Un logro que aparece al
      // editar el historial de hace un mes se añade en silencio.
      const since = addDays(ctx!.today, -1);
      const fresh = [...current.keys()].filter((k) => !seen.current!.has(k) && current.get(k)!.achievedOn >= since);
      // Los logros revocados salen del conjunto visto, para que vuelvan a avisar si se recuperan.
      seen.current = new Set(current.keys());
      await setMeta(SEEN_KEY, [...seen.current]);
      if (cancelled || fresh.length === 0) return;
      const first = current.get(fresh[0]!)!;
      const label = achievementLabel(t, fresh[0]!.split(':').slice(1).join(':'), first.unit);
      toast.show(
        fresh.length === 1
          ? t('achievements.earned', { habit: first.habit, label })
          : t('achievements.earnedMany', { habit: first.habit, label, count: fresh.length - 1 }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [current, ctx, t, toast]);
}
